import React, { useState } from 'react';
import styles from './AccountIcon.module.scss';
import AccountCircleRoundedIcon from '@mui/icons-material/AccountCircleRounded';
import KeyboardArrowDownOutlinedIcon from '@mui/icons-material/KeyboardArrowDownOutlined';
import ExitToAppIcon from '@mui/icons-material/ExitToApp';
import { Divider, Menu, MenuItem } from '@mui/material';
import { getAccountRole, getFullName } from '../../utils/dashboardUtils';
import { useSessionAction, useSessionData } from '../../providers';

import type { Manager, Trainer, Member } from '../../types';

const getAccountData = ({
  loginTrainer,
  loginMember,
  loginManager,
}: {
  loginManager: Manager | null;
  loginTrainer: Trainer | null;
  loginMember: Member | null;
}): {
  accountName: string;
  accountRole: string;
} => {
  const manager = loginManager;
  const trainer = loginTrainer;
  const member = loginMember;
  return {
    accountName: getFullName(manager || trainer || member),
    accountRole: getAccountRole(manager || undefined, trainer || undefined),
  };
};

const AccountIcon: React.FC = () => {
  const { loginManager, loginTrainer, loginMember } = useSessionData();
  const { logout } = useSessionAction();
  const { accountName, accountRole } = getAccountData({
    loginManager,
    loginTrainer,
    loginMember,
  });
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const menuOpen = Boolean(anchorEl);

  const handleToggleMenu = (event: React.MouseEvent<HTMLButtonElement>) => {
    if (menuOpen) {
      setAnchorEl(null);
      return;
    }
    setAnchorEl(event.currentTarget);
  };

  const handleCloseMenu = () => setAnchorEl(null);

  const handleLogout = () => {
    logout();
    handleCloseMenu();
  };

  const menuButtonId = 'account-menu-trigger';

  return (
    <>
      <button
        type="button"
        className={`${styles.accountWrapper} ${menuOpen ? styles.open : ''}`}
        onClick={handleToggleMenu}
        aria-haspopup="menu"
        aria-expanded={menuOpen}
        aria-controls={menuOpen ? 'account-menu' : undefined}
        id={menuButtonId}
      >
        <AccountCircleRoundedIcon className={styles.accountIcon} />
        <div className={styles.accountDetails}>
          <div className={styles.accountName}>{accountName}</div>
          <div className={styles.accountDescription}>{accountRole}</div>
        </div>
        <KeyboardArrowDownOutlinedIcon
          className={`${styles.chevron} ${menuOpen ? styles.chevronOpen : ''}`}
        />
      </button>
      <Menu
        id="account-menu"
        anchorEl={anchorEl}
        open={menuOpen}
        onClose={handleCloseMenu}
        MenuListProps={{
          'aria-labelledby': menuButtonId,
        }}
        classes={{
          paper: styles.accountMenuPaper,
          list: styles.accountMenuList,
        }}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <MenuItem disableRipple disabled className={styles.accountMenuSummary}>
          <div className={styles.summaryText}>
            <div className={styles.summaryName}>{accountName}</div>
            <div className={styles.summaryRole}>{accountRole}</div>
          </div>
        </MenuItem>
        <Divider className={styles.menuDivider} />
        <MenuItem onClick={handleLogout} className={styles.accountMenuItem}>
          <ExitToAppIcon fontSize="small" />
          <span>Log Out</span>
        </MenuItem>
      </Menu>
    </>
  );
};

export default AccountIcon;
